import { useEditContext, useRefresh } from "ra-core"
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { SaveAction } from "@/dataProvider/types"
import chainOptions from "@/utils/chainOptions"
import { Box,
  Button,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  MenuItem,
  ToggleButton, 
  ToggleButtonGroup, 
  Typography} from "@mui/material";
import { useFormik, useFormikContext } from "formik";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import { ValidationFrequency, ValidationScheduleResponseDto } from "@/gen/api/backup";
import { RecPatch } from "@/dataProvider/tools";
import PickableInput, { PickableInputProps } from "../input/PickableInput";
import { nameof } from "@/utils/typeChecks";
import { useSnackbar } from "notistack";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { merge } from "lodash";
import { add } from "date-fns";
import { tbgSxProps } from "../overrides/toggleButtonGroupSx";
import { useSelector } from "react-redux";
import { getUserProfile } from "@/reducers/userProfile";


export interface ScheduleInfo {
  frequency: ValidationFrequency;
  startHour: string;
  startMinute: string;
  interval: string;
  daysOfWeek: string[];
  dayOfMonth: string;
}

export interface BackupSchedulePickerProps {
  onClose: () => void;
}

const BackupSchedulePicker: FC<BackupSchedulePickerProps> = ({ onClose }) => {
  const { save, record } = useEditContext<ValidationScheduleResponseDto & RecPatch>({ redirect: '/dashboard' })
  const refresh = useRefresh()
  const { enqueueSnackbar } = useSnackbar()

  const performSave = useCallback<SaveAction<ValidationScheduleResponseDto>>((record, redirect, callbacks) => {
    save && save(record, redirect, chainOptions(callbacks, { 
      onSuccess: () => {
        refresh()
        onClose()
        enqueueSnackbar('Update succeed', { variant: 'success' })
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Update failed: ${errMsg}`, { variant: 'error' })
      }
    }))
  }, [save, refresh, enqueueSnackbar, onClose])

  return (
    <BackupSchedulePickerBody onSave={performSave} preData={record} onCancel={onClose} />
  )
}

export interface BackupSchedulePickerBodyProps {
  onSave: SaveAction<ValidationScheduleResponseDto>;
  onCancel: () => void;
  preData: ValidationScheduleResponseDto | undefined;
}

const validationSchema =
  Yup.object().shape({
    frequency: Yup.number().integer().required('Frequency is required')
      .oneOf([
        ValidationFrequency.Hourly, ValidationFrequency.Daily, ValidationFrequency.Weekly, ValidationFrequency.Monthly
      ]),
    interval: Yup.string().required('Interval is required'),
  })

type ErrorBag = {[k in keyof ScheduleInfo]+?:string}

const hourChoices = [...Array(24).keys()].map(i => i.toString().padStart(2, '0'))
const minuteChoices = ['00', '15', '30', '45']

const findDivisors = (n: number) => 
  [...Array(Math.trunc(n / 2)).keys()].map(i => i + 1)
    .filter(i => n % i === 0);

const hourlyPicks = findDivisors(24)
const dailyPicks = findDivisors(30)
const weekdays = [ 'SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT' ]
const monthdays = [...Array(31).keys()].map(i => (i + 1).toString())

const validate = (values: ScheduleInfo) => {
  const errors: ErrorBag = {}

  if (+values.interval > 1000 || +values.interval < 1) {
    errors['interval'] = 'Invalid interval value'
  }
  if (hourChoices.indexOf(values.startHour) < 0) {
    errors['startHour'] = 'Invalid Hour value'
  }
  if (minuteChoices.indexOf(values.startMinute) < 0) {
    errors['startMinute'] = 'Invalid Minute value'
  }
  switch(values.frequency) {
    case ValidationFrequency.Hourly: { break; }
    case ValidationFrequency.Daily: { break; }
    case ValidationFrequency.Weekly: {
      if (values.daysOfWeek.length === 0) {
        errors['daysOfWeek'] = 'Specify at least 1 day in week'
      } else if (values.daysOfWeek.some(dw => weekdays.indexOf(dw) < 0)) {
        errors['daysOfWeek'] = 'Invalid day(s) of week value'
      } else if (+values.interval > 1 && values.daysOfWeek.length > 1) {
        errors['daysOfWeek'] = 'Allow only 1 day in week'
      }
      break;
    }
    case ValidationFrequency.Monthly: {
      if (monthdays.indexOf(values.dayOfMonth) < 0) {
        errors['dayOfMonth'] = 'Invalid day of month value'
      }
      break;
    }
  }
  return errors;
}

export const initValueGen = (preData: ValidationScheduleResponseDto | undefined) => {
  const iValue = {
    submit: '',
    frequency: preData?.frequency || ValidationFrequency.Hourly,
    startHour: (preData?.startHour || 0).toString().padStart(2, '0'),
    startMinute: (preData?.startMinute || 0).toString().padStart(2, '0'),
    interval: (preData?.interval || 1).toString(),
    daysOfWeek: 
      preData?.daysOfWeek 
      ? preData.daysOfWeek.split(',').map(s => s.replace(' ', '')) 
      : [],
    dayOfMonth: (preData?.dayOfMonth || 1).toString(),
  }
  return iValue
}

export const BackupSchedulePickerBody: FC<BackupSchedulePickerBodyProps> = (props) => {
  const user = useSelector(getUserProfile)
  
  const { onSave, onCancel, preData } = props
  const initialValues = initValueGen(preData)
  
    const offsetToUTC = 
      preData?.timeZoneOffset !== undefined ? Number.parseInt(preData.timeZoneOffset || '0')
      : user?.timeZoneOffset ? (user.timeZoneOffset || 0) 
      : new Date().getTimezoneOffset()
    const offsetHr = Math.abs(Math.floor(offsetToUTC / 60)).toString().padStart(2, '0')
    const offsetMn = Math.abs(offsetToUTC % 60).toString().padStart(2, '0')
    const timezoneTipText = `UTC${offsetToUTC == Math.abs(offsetToUTC) ? '+' : '-'}${offsetHr}:${offsetMn}`

  const preDataRef = useRef<boolean | null>(!!preData)

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    const newScheduleInfo: Partial<ValidationScheduleResponseDto> = { 
      // id: preData?.databaseId,
      databaseId: preData?.databaseId,
      frequency: values.frequency,  
      interval: +values.interval,
      startHour: +values.startHour,
      startMinute: +values.startMinute,
    }
    merge(newScheduleInfo,
      values.frequency === ValidationFrequency.Weekly
      ? { daysOfWeek: values.daysOfWeek.join(',') }
      : values.frequency === ValidationFrequency.Monthly
      ? { dayOfMonth: values.dayOfMonth }
      : {})

    onSave(newScheduleInfo, '', options)
  }, [onSave, preData?.databaseId])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema, validate })
  const { errors, isSubmitting, values, setFieldValue, setValues } = formikCtrl

  useEffect(() => {
    if (!preDataRef.current && !!preData) {
      preDataRef.current = true
      setValues(initValueGen(preData))
    }
  }, [preData, setValues])

  useEffect(() => {
    if (preData) {
      if(values.frequency === preData.frequency && +values.interval !== preData.interval) {
        setFieldValue('interval', preData.interval)
      }
    } else {
      setFieldValue('interval', 1)
    }
  }, [preData, values.frequency, setFieldValue]) // eslint-disable-line

  return (
    // <Card>
    <OdForm formikCtrl={formikCtrl}>

        <CardContent>
          <TextField 
            label="Frequency*" 
            name="frequency" 
            select
            size="small"
          >
            <MenuItem value={ValidationFrequency.Hourly}>Hourly</MenuItem>
            <MenuItem value={ValidationFrequency.Daily}>Daily</MenuItem>
            <MenuItem value={ValidationFrequency.Weekly}>Weekly</MenuItem>
            <MenuItem value={ValidationFrequency.Monthly}>Monthly</MenuItem>
          </TextField>
          <Box 
            sx={{
              mx: -2,
              mt: 2,
              p: 2,
            }}
          >
            {values.frequency === ValidationFrequency.Hourly
            ? <HourInterval />
            : values.frequency === ValidationFrequency.Daily
            ? <DayInterval />
            : values.frequency === ValidationFrequency.Weekly
            ? <WeekInterval />
            : values.frequency === ValidationFrequency.Monthly
            ? <MonthInterval />
            : null
            }              
          </Box>
          <Box 
            sx={{
              mx: -2,
              px: 2,
              pt: 1,
              pb: 2,
              display: 'flex',
              alignItems: 'baseline'
            }}
            id="start-time-picker-form"
          >
            <Typography
              component="span"
              color="textPrimary"
              variant="subtitle2"
            >
              Start Time
            </Typography>
            <FormControl>
              <FormikPickableInput
                allowEdit={false}
                name="startHour"
                choices={hourChoices}
                choiceRowLength={6}
                maxRows={5}
                aria-describedby="start-time-picker-form"
              />
            </FormControl>
            <Typography
              component="span"
              variant="body2"
            >
              :
            </Typography>
            <FormControl>
              <FormikPickableInput
                allowEdit={false}
                name="startMinute"
                choices={minuteChoices}
                choiceRowLength={4}
                aria-describedby="start-time-picker-form"
              />
            </FormControl>
            <Typography 
              component="span"
              variant="caption"
              sx={{ pl: 2 }}
            >
              ({timezoneTipText})
            </Typography>
          </Box>
          {/* <Divider sx={{ mx: -2 }} />
          <Box
            sx={{
              display: 'flex',
              pt: 2,
            }}
          >
            <Typography
              color="textPrimay"
              variant="subtitle2"
              sx={{ flex: 1 }}
            >
              Estimated Billing Amount ($/month)
            </Typography>
            <Typography
              variant="body2"
              sx={{ flex: 1 }}
            >
              $40 - test
            </Typography>
          </Box> */}
          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>
                {errors.submit}
              </FormHelperText>
            </Box>
          )}
        </CardContent>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 2
          }}
        >
          <Button
            color="primary"
            disabled={isSubmitting}
            onClick={onCancel}
            variant="text"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={isSubmitting}
            type="submit"
            variant="contained"
          >
            Update Schedule
          </Button>
        </Box>

    </OdForm>
    // </Card>
  )


}

export default BackupSchedulePicker;


export const FormikPickableInput = ({
  name,
  ...props
}: Omit<PickableInputProps, 'name'> & {name: keyof ScheduleInfo}) => {
  const {
    touched,
    errors,
    values,
    handleBlur,
    handleChange,
  } = useFormikContext<ScheduleInfo>()
  return (
    <Box
      sx={{
        mx: 1,
        borderRadius: '6px',
        bgcolor: 'background.paper'
      }}
    >
      <PickableInput 
        name={name}
        error={Boolean(touched[name] && errors[name])}
        value={values[name]}
        onBlur={handleBlur}
        onChange={handleChange}
        inputMode="numeric"
        inputProps={{ pattern: '[0-9]*', inputMode: 'numeric', maxLength: 3 }}
        {...props}
      />
    </Box>
  )
}


export const HourInterval: FC = () => {
  const {
    touched,
    errors,
  } = useFormikContext<ScheduleInfo>()
  
  return (
    <FormControl id="hours-picker-form"
      sx={{ flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap' }}
    >
      <Typography variant="body2">
        Every 
      </Typography>
      <FormikPickableInput 
        allowEdit={false}
        name="interval"
        choices={hourlyPicks}
        choiceRowLength={4}
        aria-describedby="hours-picker-form"
      />
      <Typography variant="body2">
        hour(s)
      </Typography>
      {touched.interval && errors.interval ?
        <FormHelperText sx={{ color: 'error.main' }}>
          {errors.interval}
        </FormHelperText> : null
      }
    </FormControl>
  )
}
export const DayInterval: FC = () => {
  const {
    touched,
    errors,
  } = useFormikContext<ScheduleInfo>()
  
  return (
    <FormControl id="days-picker-form"
      sx={{ flexDirection: 'row', alignItems: 'baseline' }}
    >
      <Typography variant="body2">
        Every 
      </Typography>
      <FormikPickableInput 
        allowEdit={false}
        name="interval"
        choices={dailyPicks}
        choiceRowLength={4}
        aria-describedby="days-picker-form"
      />
      <Typography variant="body2">
        days(s)
      </Typography>
      {touched.interval && errors.interval ?
        <FormHelperText sx={{ color: 'error.main' }}>
          {errors.interval}
        </FormHelperText> : null
      }
    </FormControl>
  )
}
export const WeekInterval: FC = () => {
  const {
    values,
    handleChange,
  } = useFormikContext<ScheduleInfo>()

  const handleWeekDaysSelect = useCallback((e:any, selectedDays: string[]) => {
    const mimicEvent: Event = {
      target: {
        name: nameof<ScheduleInfo>('daysOfWeek'),
        value: selectedDays,
      }
    } as any
    handleChange(mimicEvent)
  }, [handleChange])

  return (
    <>
      <Typography variant="body2" className="daysInWeek-label">
        Every week on
      </Typography>
      <FormControl
        sx={{ 
          display: 'block',
          mt: 1,
          ml: 2,
        }}
        className="daysInWeek"
      >
        <ToggleButtonGroup
          value={values.daysOfWeek}
          onChange={handleWeekDaysSelect}
          aria-label="select days of week"
          size="small"
          sx={{...tbgSxProps}}
        >
          <ToggleButton value="SUN" aria-label="sunday">
            <Typography variant="subtitle2" color="textSecondary">S</Typography>
          </ToggleButton>
          <ToggleButton value="MON" aria-label="monday">
            <Typography variant="subtitle2" color="textSecondary">M</Typography>
          </ToggleButton>
          <ToggleButton value="TUE" aria-label="tuesday">
            <Typography variant="subtitle2" color="textSecondary">T</Typography>
          </ToggleButton>
          <ToggleButton value="WED" aria-label="wednesday">
            <Typography variant="subtitle2" color="textSecondary">W</Typography>
          </ToggleButton>
          <ToggleButton value="THU" aria-label="thursday">
            <Typography variant="subtitle2" color="textSecondary">T</Typography>
          </ToggleButton>
          <ToggleButton value="FRI" aria-label="friday">
            <Typography variant="subtitle2" color="textSecondary">F</Typography>
          </ToggleButton>
          <ToggleButton value="SAT" aria-label="saturday">
            <Typography variant="subtitle2" color="textSecondary">S</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
    </>
  )
}
export const MonthInterval: FC = () => {
  const {
    touched,
    errors,
  } = useFormikContext<ScheduleInfo>()
  const monthChoices = useMemo(() => [1, 2, 3, 4, 6], [])

  return (
    <>
      <FormControl id="months-picker-form"
        sx={{ flexDirection: 'row', alignItems: 'baseline' }}
        className="daysInMonth-label"
      >
        <Typography variant="body2">
          Every 
        </Typography>
        <FormikPickableInput 
          allowEdit={false}
          name="interval"
          choices={monthChoices}
          choiceRowLength={3}
          aria-describedby="months-picker-form"
        />
        <Typography variant="body2">
          month(s) on
        </Typography>
        {touched.interval && errors.interval ?
          <FormHelperText sx={{ color: 'error.main' }}>
            {errors.interval}
          </FormHelperText> : null
        }
      </FormControl>
      <FormControl id="day-in-month-picker-form"
        sx={{ 
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'baseline',
          mt: 1,
          ml: 2,
        }}
        className="daysInMonth"
      >
        <Typography variant="body2" component="span" sx={{ ml: -0.5 }}>day</Typography>
        <FormikPickableInput 
          allowEdit={false}
          name="dayOfMonth"
          choices={monthdays}
          choiceRowLength={5}
          maxRowsCount={7}
          aria-describedby="day-in-month-picker-form"
        />

      </FormControl>
    </>
  )
}


